import React, { useState, useEffect } from "react";
import { Container, Card, Form, Button, Row, Col, Spinner, Image } from "react-bootstrap";
import { FaSave, FaArrowLeft, FaUpload, FaTrash, FaTimes } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../../../components/common/Header";
import ProjectService from "../../../services/projectService";

const AdminEditProject = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({ categories: [], languages: [] });

  // Form State
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [demoUrl, setDemoUrl] = useState("");
  const [githubUrl, setGithubUrl] = useState("");
  
  // File States
  const [zipFile, setZipFile] = useState(null);
  const [currentZipName, setCurrentZipName] = useState("");
  
  const [docxFile, setDocxFile] = useState(null);
  const [currentDocxName, setCurrentDocxName] = useState("");

  const [pdfFile, setPdfFile] = useState(null);
  const [currentPdfName, setCurrentPdfName] = useState("");
  
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  
  // Array States
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  
  // Images
  const [existingImages, setExistingImages] = useState([]); // from DB {id, url}
  const [deletedImages, setDeletedImages] = useState([]); // IDs to delete
  const [newImages, setNewImages] = useState([]); // New files
  const [newImagePreviews, setNewImagePreviews] = useState([]);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Load filters
      const filterRes = await ProjectService.getProjectFilters();
      setFilters(filterRes.data?.data || { categories: [], languages: [] });

      // Load project details
      const projRes = await ProjectService.getProjectById(id);
      const proj = projRes.data?.data;
      
      if (!proj) {
        toast.error("Project not found");
        navigate("/admin/projects");
        return;
      }

      setTitle(proj.title || "");
      setDescription(proj.description || "");
      setPrice(proj.price || "");
      setVideoUrl(proj.video_url || "");
      setDemoUrl(proj.demo_url || "");
      setGithubUrl(proj.github_url || "");
      setCurrentZipName(proj.zip_file_name || "");
      setCurrentDocxName(proj.docx_file_name || "");
      setCurrentPdfName(proj.pdf_file_name || "");
      
      if (proj.thumbnail_url) {
        setThumbnailPreview(proj.thumbnail_url);
      }

      // Map existing images - wait, backend Project.php getImages only returns URLs!
      // Let's modify the backend to return id and url if possible, or just delete by URL.
      // But actually, we don't even have an ID if getImages only returns strings.
      // We will just let them delete by index or URL later, but for now let's say they can't delete individual old images easily unless we fix the backend.
      // Wait, we need to send the URLs to delete.
      setExistingImages(proj.images || []);
      
      // Parse categories and languages back to IDs based on names
      // proj.categories is array of strings (names)
      const catIds = [];
      const langIds = [];
      const allCats = filterRes.data?.data?.categories || [];
      const allLangs = filterRes.data?.data?.languages || [];
      
      if (proj.categories) {
        proj.categories.forEach(catName => {
          const found = allCats.find(c => c.name === catName);
          if (found) catIds.push(found.id);
        });
      }
      
      if (proj.languages) {
        proj.languages.forEach(langName => {
          const found = allLangs.find(l => l.name === langName);
          if (found) langIds.push(found.id);
        });
      }
      
      setSelectedCategories(catIds);
      setSelectedLanguages(langIds);

    } catch (error) {
      console.error(error);
      toast.error("Failed to load project details");
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (e) => {
    const value = parseInt(e.target.value);
    if (e.target.checked) setSelectedCategories([...selectedCategories, value]);
    else setSelectedCategories(selectedCategories.filter((id) => id !== value));
  };

  const handleLanguageChange = (e) => {
    const value = parseInt(e.target.value);
    if (e.target.checked) setSelectedLanguages([...selectedLanguages, value]);
    else setSelectedLanguages(selectedLanguages.filter((id) => id !== value));
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Thumbnail too large (Max 2MB)");
        return;
      }
      setThumbnail(file);
      const reader = new FileReader();
      reader.onloadend = () => setThumbnailPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleNewImageChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
        toast.error(`Invalid file type: ${file.name}`);
        return false;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`File too large: ${file.name} (Max 2MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setNewImages(prev => [...prev, ...validFiles]);
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => setNewImagePreviews(prev => [...prev, reader.result]);
        reader.readAsDataURL(file);
      });
    }
  };

  const removeNewImage = (index) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
    setNewImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (url, index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
    setDeletedImages(prev => [...prev, url]);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!title || !description || !price) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      formData.append("price", price);
      formData.append("video_url", videoUrl);
      formData.append("demo_url", demoUrl);
      formData.append("github_url", githubUrl);
      
      if (zipFile) formData.append("zip_file", zipFile);
      if (docxFile) formData.append("docx_file", docxFile);
      if (pdfFile) formData.append("pdf_file", pdfFile);
      if (thumbnail) formData.append("thumbnail", thumbnail);
      
      formData.append("categories", JSON.stringify(selectedCategories));
      formData.append("languages", JSON.stringify(selectedLanguages));

      newImages.forEach((img) => formData.append("images[]", img));
      formData.append("deleted_images", JSON.stringify(deletedImages));
      
      await ProjectService.adminUpdateProject(id, formData);
      toast.success("Project updated successfully");
      navigate("/admin/projects");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update project");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-light min-vh-100 d-flex flex-column">
        <Header />
        <div className="flex-grow-1 d-flex justify-content-center align-items-center">
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      <Header />
      <Container className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Edit Project</h4>
          <Button variant="outline-secondary" onClick={() => navigate("/admin/projects")}>
            <FaArrowLeft className="me-2" /> Back to Projects
          </Button>
        </div>

        <Form onSubmit={handleSave}>
          <Row>
            <Col md={8}>
              <Card className="shadow-sm mb-4 border-0 rounded-4">
                <Card.Body className="p-4">
                  <h5 className="mb-4">Project Details</h5>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Project Title <span className="text-danger">*</span></Form.Label>
                    <Form.Control required value={title} onChange={(e) => setTitle(e.target.value)} />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Price (₹) <span className="text-danger">*</span></Form.Label>
                        <Form.Control type="number" required value={price} onChange={(e) => setPrice(e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Source Code (ZIP)</Form.Label>
                        <Form.Control type="file" accept=".zip" onChange={(e) => setZipFile(e.target.files[0])} />
                        {currentZipName && <small className="text-muted">Current: {currentZipName}</small>}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Project Report (DOCX)</Form.Label>
                        <Form.Control type="file" accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(e) => setDocxFile(e.target.files[0])} />
                        {currentDocxName && <small className="text-muted">Current: {currentDocxName}</small>}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Project Report (PDF)</Form.Label>
                        <Form.Control type="file" accept=".pdf,application/pdf" onChange={(e) => setPdfFile(e.target.files[0])} />
                        {currentPdfName && <small className="text-muted">Current: {currentPdfName}</small>}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>YouTube Demo URL</Form.Label>
                        <Form.Control value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Live Demo URL</Form.Label>
                        <Form.Control value={demoUrl} onChange={(e) => setDemoUrl(e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>GitHub URL</Form.Label>
                        <Form.Control value={githubUrl} onChange={(e) => setGithubUrl(e.target.value)} />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Description (Raw HTML) <span className="text-danger">*</span></Form.Label>
                    <Form.Control as="textarea" rows={10} required value={description} onChange={(e) => setDescription(e.target.value)} style={{ fontFamily: 'monospace' }} />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="shadow-sm mb-4 border-0 rounded-4">
                <Card.Body className="p-4">
                  <h5 className="mb-4">Taxonomy</h5>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold text-muted small text-uppercase">Categories</Form.Label>
                    <div className="border p-3 rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {filters.categories.map((cat) => (
                        <Form.Check key={cat.id} type="checkbox" id={`cat-${cat.id}`} label={cat.name} value={cat.id} checked={selectedCategories.includes(cat.id)} onChange={handleCategoryChange} />
                      ))}
                    </div>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold text-muted small text-uppercase">Tech Stack</Form.Label>
                    <div className="border p-3 rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {filters.languages.map((lang) => (
                        <Form.Check key={lang.id} type="checkbox" id={`lang-${lang.id}`} label={lang.name} value={lang.id} checked={selectedLanguages.includes(lang.id)} onChange={handleLanguageChange} />
                      ))}
                    </div>
                  </Form.Group>
                </Card.Body>
              </Card>

              <Card className="shadow-sm mb-4 border-0 rounded-4">
                <Card.Body className="p-4">
                  <h5 className="mb-3">Hero Image (Thumbnail)</h5>
                  <Form.Group className="mb-3">
                    <Form.Control type="file" accept="image/jpeg,image/png,image/jpg,image/webp" onChange={handleThumbnailChange} />
                  </Form.Group>
                  {thumbnailPreview && (
                    <div className="position-relative mb-3" style={{ width: '100%', height: '160px' }}>
                      <Image src={thumbnailPreview} alt="thumbnail" rounded style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  )}
                </Card.Body>
              </Card>

              <Card className="shadow-sm mb-4 border-0 rounded-4">
                <Card.Body className="p-4">
                  <h5 className="mb-3">Add More Images</h5>
                  <Form.Group className="mb-3">
                    <Form.Control type="file" accept="image/jpeg,image/png,image/jpg,image/webp" multiple onChange={handleNewImageChange} />
                  </Form.Group>
                  
                  {newImagePreviews.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mt-3">
                      {newImagePreviews.map((preview, index) => (
                        <div key={index} className="position-relative" style={{ width: '80px', height: '80px' }}>
                          <Image src={preview} alt="preview" thumbnail style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <Button variant="danger" size="sm" className="position-absolute top-0 end-0 p-0 rounded-circle" style={{ width: '20px', height: '20px', transform: 'translate(30%, -30%)' }} onClick={() => removeNewImage(index)}>
                            <FaTimes size={10} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {existingImages.length > 0 && (
                    <div className="mt-3 border-top pt-3">
                      <p className="small text-muted mb-2">Existing Images</p>
                      <div className="d-flex flex-wrap gap-2">
                        {existingImages.map((url, index) => (
                          <div key={index} className="position-relative" style={{ width: '80px', height: '80px' }}>
                            <Image src={url} alt="existing" thumbnail style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            <Button variant="danger" size="sm" className="position-absolute top-0 end-0 p-0 rounded-circle" style={{ width: '20px', height: '20px', transform: 'translate(30%, -30%)' }} onClick={() => handleRemoveExistingImage(url, index)}>
                              <FaTimes size={10} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>

              <div className="d-grid gap-2 mt-4">
                <Button variant="primary" size="lg" type="submit" disabled={saving} className="py-3 rounded-3 shadow">
                  {saving ? <><Spinner size="sm" className="me-2" /> Saving...</> : <><FaSave className="me-2" /> Update Project</>}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default AdminEditProject;
